// @ts-nocheck
import React, { useState } from 'react';

interface CollectionItem {
  _id: string;
  name: string;
  color?: string;
}

interface FeedItem {
  _id: string;
  title: string;
  feedUrl: string;
  siteUrl?: string;
  status: 'active' | 'paused' | 'error';
  unreadCount?: number;
  lastSuccessfulFetchAt?: string;
  lastError?: string;
  consecutiveFailures?: number;
  collectionIds?: string[];
}

interface ManageFeedsModalProps {
  isOpen: boolean;
  onClose: () => void;
  feeds: FeedItem[];
  collections?: CollectionItem[];
  onRefreshFeed: (feedId: string) => Promise<any>;
  onToggleStatus: (feed: FeedItem) => Promise<any>;
  onDeleteFeed: (feedId: string) => Promise<any>;
  onUpdateTitle: (feedId: string, title: string) => Promise<any>;
  onUpdateCollections?: (feedId: string, collectionIds: string[]) => Promise<any>;
}

export default function ManageFeedsModal({
  isOpen,
  onClose,
  feeds = [],
  collections = [],
  onRefreshFeed,
  onToggleStatus,
  onDeleteFeed,
  onUpdateTitle,
  onUpdateCollections,
}: ManageFeedsModalProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [refreshingId, setRefreshingId] = useState<string | null>(null);
  const [editingColFeedId, setEditingColFeedId] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleStartEdit = (feed: FeedItem) => {
    setEditingId(feed._id);
    setEditingTitle(feed.title);
  };

  const handleSaveEdit = async (feedId: string) => {
    if (editingTitle.trim()) {
      await onUpdateTitle(feedId, editingTitle.trim());
    }
    setEditingId(null);
  };

  const handleRefresh = async (feedId: string) => {
    setRefreshingId(feedId);
    try {
      await onRefreshFeed(feedId);
    } finally {
      setRefreshingId(null);
    }
  };

  const handleToggleFeedCol = async (feed: FeedItem, colId: string) => {
    if (!onUpdateCollections) return;
    const current = (feed.collectionIds || []).map((c) =>
      typeof c === 'object' && c && c._id ? c._id : String(c)
    );
    const updated = current.includes(colId)
      ? current.filter((id) => id !== colId)
      : [...current, colId];
    await onUpdateCollections(feed._id, updated);
  };

  return (
    <div className="reader-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="reader-modal-card" style={{ maxWidth: '640px' }} onClick={(e) => e.stopPropagation()}>
        <div className="reader-modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '6px',
                background: 'var(--reader-hover)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--reader-accent)',
              }}
            >
              <i className="fi fi-rr-settings" style={{ fontSize: '0.85rem' }} />
            </div>
            <h3 className="reader-modal-title">Manage Subscribed Feeds ({feeds.length})</h3>
          </div>
          <button
            onClick={onClose}
            aria-label="Close modal"
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.25rem',
              cursor: 'pointer',
              color: 'var(--reader-text-muted)',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        <div className="reader-modal-body" style={{ maxHeight: '70vh', padding: 0 }}>
          {feeds.length === 0 ? (
            <div className="reader-empty-state">
              <i className="fi fi-rr-rss reader-empty-icon" />
              <div className="reader-empty-title">No feed subscriptions</div>
              <div className="reader-empty-subtitle">
                Subscribe to blogs, news outlets, and podcasts to see them managed here.
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {feeds.map((feed) => {
                const feedColIds = (feed.collectionIds || []).map((c) =>
                  typeof c === 'object' && c && c._id ? c._id : String(c)
                );
                const isEditingCols = editingColFeedId === feed._id;

                return (
                  <div
                    key={feed._id}
                    style={{
                      padding: '12px 16px',
                      borderBottom: '1px solid var(--reader-border)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '12px',
                      }}
                    >
                      <div style={{ minWidth: 0, flex: 1 }}>
                        {editingId === feed._id ? (
                          <div style={{ display: 'flex', gap: '6px', marginBottom: '4px' }}>
                            <input
                              type="text"
                              className="reader-input"
                              style={{ height: '30px', fontSize: '0.85rem' }}
                              value={editingTitle}
                              onChange={(e) => setEditingTitle(e.target.value)}
                              autoFocus
                            />
                            <button
                              type="button"
                              className="reader-btn reader-btn-primary"
                              style={{ height: '30px', padding: '0 8px' }}
                              onClick={() => handleSaveEdit(feed._id)}
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              className="reader-btn"
                              style={{ height: '30px', padding: '0 8px' }}
                              onClick={() => setEditingId(null)}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{feed.title}</span>
                            <button
                              onClick={() => handleStartEdit(feed)}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: 'var(--reader-text-subtle)',
                                padding: 0,
                              }}
                              title="Rename feed"
                            >
                              <i className="fi fi-rr-edit" style={{ fontSize: '0.75rem' }} />
                            </button>
                            <span
                              style={{
                                fontSize: '0.68rem',
                                padding: '1px 6px',
                                borderRadius: '3px',
                                textTransform: 'uppercase',
                                fontWeight: 600,
                                backgroundColor:
                                  feed.status === 'active'
                                    ? 'rgba(34, 197, 94, 0.15)'
                                    : feed.status === 'paused'
                                    ? 'rgba(234, 179, 8, 0.15)'
                                    : 'rgba(239, 68, 68, 0.15)',
                                color:
                                  feed.status === 'active'
                                    ? '#16a34a'
                                    : feed.status === 'paused'
                                    ? '#ca8a04'
                                    : '#dc2626',
                              }}
                            >
                              {feed.status}
                            </span>
                          </div>
                        )}

                        <div
                          style={{
                            fontSize: '0.75rem',
                            color: 'var(--reader-text-subtle)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {feed.feedUrl}
                        </div>

                        {feed.lastError && (
                          <div style={{ fontSize: '0.72rem', color: '#ef4444', marginTop: '2px' }}>
                            Error: {feed.lastError}
                          </div>
                        )}
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {collections.length > 0 && onUpdateCollections && (
                          <button
                            className="reader-btn"
                            style={{ fontSize: '0.74rem', height: '28px', padding: '0 8px' }}
                            onClick={() => setEditingColFeedId(isEditingCols ? null : feed._id)}
                            title="Move feed to collections"
                          >
                            <i className="fi fi-rr-folder" style={{ fontSize: '0.7rem' }} />
                            <span>Collections ({feedColIds.length})</span>
                          </button>
                        )}

                        <button
                          className="reader-btn-icon"
                          onClick={() => handleRefresh(feed._id)}
                          disabled={refreshingId === feed._id}
                          title="Refresh feed now"
                        >
                          <i
                            className="fi fi-rr-refresh"
                            style={{ animation: refreshingId === feed._id ? 'spin 1s linear infinite' : 'none' }}
                          />
                        </button>

                        <button
                          className="reader-btn-icon"
                          onClick={() => onToggleStatus(feed)}
                          title={feed.status === 'paused' ? 'Resume feed' : 'Pause feed'}
                        >
                          <i className={`fi fi-rr-${feed.status === 'paused' ? 'play' : 'pause'}`} />
                        </button>

                        <button
                          className="reader-btn-icon"
                          onClick={() => onDeleteFeed(feed._id)}
                          title="Unsubscribe / delete feed"
                          style={{ color: '#ef4444' }}
                        >
                          <i className="fi fi-rr-trash" />
                        </button>
                      </div>
                    </div>

                    {/* Assigned Collections Pills */}
                    {!isEditingCols && feedColIds.length > 0 && (
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.72rem', color: 'var(--reader-text-subtle)' }}>Collections:</span>
                        {feedColIds.map((cid) => {
                          const col = collections.find((c) => c._id === cid);
                          if (!col) return null;
                          return (
                            <span
                              key={col._id}
                              className="reader-item-col-pill"
                              style={{ fontSize: '0.72rem' }}
                            >
                              <span
                                className="reader-col-pill-dot"
                                style={{ backgroundColor: col.color || 'var(--reader-accent)' }}
                              />
                              {col.name}
                            </span>
                          );
                        })}
                      </div>
                    )}

                    {/* Inline Collection Selector when active */}
                    {isEditingCols && (
                      <div
                        style={{
                          padding: '8px 10px',
                          background: 'var(--reader-hover)',
                          borderRadius: '6px',
                          border: '1px solid var(--reader-border)',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '6px',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--reader-text)' }}>
                            Move Feed & Items to Collections:
                          </span>
                          <button
                            type="button"
                            onClick={() => setEditingColFeedId(null)}
                            style={{
                              background: 'none',
                              border: 'none',
                              fontSize: '0.72rem',
                              color: 'var(--reader-accent)',
                              cursor: 'pointer',
                              padding: 0,
                            }}
                          >
                            Done
                          </button>
                        </div>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          {collections.map((col) => {
                            const isSelected = feedColIds.includes(col._id);
                            return (
                              <button
                                key={col._id}
                                type="button"
                                onClick={() => handleToggleFeedCol(feed, col._id)}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '5px',
                                  padding: '3px 8px',
                                  borderRadius: '4px',
                                  border: isSelected
                                    ? '1.5px solid var(--reader-accent)'
                                    : '1px solid var(--reader-border)',
                                  background: isSelected
                                    ? 'var(--reader-accent-light)'
                                    : 'var(--reader-surface)',
                                  color: isSelected ? 'var(--reader-accent)' : 'var(--reader-text)',
                                  fontSize: '0.75rem',
                                  fontWeight: isSelected ? 600 : 400,
                                  cursor: 'pointer',
                                }}
                              >
                                <span
                                  style={{
                                    width: '7px',
                                    height: '7px',
                                    borderRadius: '2px',
                                    backgroundColor: col.color || 'var(--reader-accent)',
                                  }}
                                />
                                <span>{col.name}</span>
                                {isSelected && <i className="fi fi-rr-check" style={{ fontSize: '0.65rem' }} />}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="reader-modal-footer">
          <button type="button" className="reader-btn" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
